import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlockingMutation } from '../hooks/useBlockingMutation';
import { Window, Button, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { settingsApi, ApiClientError, type FingerprintChecksSettings, type FingerprintCheckValue, type NumericCheckConfig } from '../api/client';
import { formatTimestamp } from '../utils/formatTimestamp';
import styles from './Settings.module.css';

type CheckName = keyof FingerprintChecksSettings;

export function Settings() {
  const queryClient = useQueryClient();
  const [pendingChanges, setPendingChanges] = useState<Partial<FingerprintChecksSettings>>({});
  const [error, setError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['fingerprint-settings'],
    queryFn: () => settingsApi.getFingerprintSettings(),
  });

  const updateMutation = useBlockingMutation({
    loadingMessage: 'Saving settings...',
    mutationFn: (settings: Partial<FingerprintChecksSettings>) =>
      settingsApi.updateFingerprintSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fingerprint-settings'] });
      setPendingChanges({});
      setError('');
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to save settings');
    },
  });

  const resetMutation = useBlockingMutation({
    loadingMessage: 'Resetting to defaults...',
    mutationFn: () => settingsApi.resetFingerprintDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fingerprint-settings'] });
      setPendingChanges({});
      setShowResetConfirm(false);
      setError('');
    },
    onError: (err) => {
      setError(err instanceof ApiClientError ? err.message : 'Failed to reset settings');
    },
  });

  const settings = data?.settings;
  const descriptions = data?.descriptions;
  const confidenceChecks = data?.confidenceChecks ?? [];
  const numericChecks = data?.numericChecks ?? {};

  const isConfidenceCheck = (checkName: CheckName): boolean => {
    return confidenceChecks.includes(checkName);
  };

  const getNumericConfig = (checkName: CheckName): NumericCheckConfig | null => {
    return numericChecks[checkName] ?? null;
  };

  const handleNumericChange = (checkName: CheckName, value: number) => {
    if (!settings) return;

    const config = getNumericConfig(checkName);
    if (!config) return;

    // Clamp to valid range
    const clamped = Math.min(config.max, Math.max(config.min, value));

    if (clamped === settings[checkName]) {
      const updated = { ...pendingChanges };
      delete updated[checkName];
      setPendingChanges(updated);
    } else {
      setPendingChanges({ ...pendingChanges, [checkName]: clamped });
    }
  };

  const handleNumericToggle = (checkName: CheckName) => {
    if (!settings) return;
    const config = getNumericConfig(checkName);
    if (!config) return;

    const currentValue = pendingChanges[checkName] ?? settings[checkName];
    const newValue: FingerprintCheckValue = currentValue === false ? config.default : false;

    if (newValue === settings[checkName]) {
      const updated = { ...pendingChanges };
      delete updated[checkName];
      setPendingChanges(updated);
    } else {
      setPendingChanges({ ...pendingChanges, [checkName]: newValue });
    }
  };

  const handleToggle = (checkName: CheckName) => {
    if (!settings) return;
    // Confidence checks use the dropdown, not the toggle
    if (isConfidenceCheck(checkName)) return;

    const currentValue = pendingChanges[checkName] ?? settings[checkName];
    const newValue = !currentValue;

    // Check if this change matches the original value
    if (newValue === settings[checkName]) {
      // Remove from pending changes
      const updated = { ...pendingChanges };
      delete updated[checkName];
      setPendingChanges(updated);
    } else {
      setPendingChanges({ ...pendingChanges, [checkName]: newValue });
    }
  };

  const handleConfidenceChange = (checkName: CheckName, newValue: FingerprintCheckValue) => {
    if (!settings) return;

    if (newValue === settings[checkName]) {
      const updated = { ...pendingChanges };
      delete updated[checkName];
      setPendingChanges(updated);
    } else {
      setPendingChanges({ ...pendingChanges, [checkName]: newValue });
    }
  };

  const handleSave = () => {
    if (Object.keys(pendingChanges).length === 0) return;
    setError('');
    updateMutation.mutate(pendingChanges);
  };

  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const confirmReset = () => {
    setError('');
    resetMutation.mutate();
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  const checkOrder: CheckName[] = [
    'botd',
    'tampering',
    'lowConfidence',
    'vpn',
    'tor',
    'proxy',
    'ipBlocklist',
    'virtualMachine',
    'suspectScore',
    'incognito',
    'developerTools',
    'remoteControl',
    'locationSpoofing',
    'emulator',
    'frida',
    'clonedApp',
  ];

  const getCheckValue = (checkName: CheckName): FingerprintCheckValue => {
    if (!settings) return true;
    return pendingChanges[checkName] ?? settings[checkName];
  };

  const isCheckModified = (checkName: CheckName): boolean => {
    return checkName in pendingChanges;
  };

  // Map FingerprintCheckValue to select dropdown value string
  const confidenceSelectValue = (value: FingerprintCheckValue): string => {
    if (value === false) return 'false';
    if (value === true) return 'low'; // legacy true = block on any detection = 'low'
    if (typeof value === 'number') return 'low'; // numeric values shouldn't appear in confidence selects
    return value;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Security Settings</h1>
      </div>

      <p className={styles.description}>
        Configure device verification checks for registration and login.
        Disabled checks will not block users even if the condition is detected.
      </p>

      {error && <div className={styles.error}>{error}</div>}

      {isLoading ? (
        <div className={styles.loading}>Loading settings...</div>
      ) : settings && descriptions ? (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Device Verification Checks</h2>
            {data?.updated_at && (
              <p className={styles.lastUpdated}>
                Last updated: {formatTimestamp(data.updated_at)}
              </p>
            )}

            <div className={styles.tableWrapper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader className={styles.enabledColumn}>Enabled</TableHeader>
                    <TableHeader className={styles.checkNameColumn}>Check Name</TableHeader>
                    <TableHeader>Description</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {checkOrder.map((checkName) => (
                    <TableRow key={checkName} selected={isCheckModified(checkName)}>
                      <TableCell className={styles.enabledCell}>
                        {isConfidenceCheck(checkName) ? (
                          <select
                            className={styles.confidenceSelect}
                            value={confidenceSelectValue(getCheckValue(checkName))}
                            onChange={(e) => {
                              const val = e.target.value;
                              const mapped: FingerprintCheckValue =
                                val === 'false' ? false :
                                val === 'low' ? 'low' :
                                val === 'medium' ? 'medium' :
                                'high';
                              handleConfidenceChange(checkName, mapped);
                            }}
                          >
                            <option value="false">Off</option>
                            <option value="low">Low+ (any)</option>
                            <option value="medium">Medium+</option>
                            <option value="high">High only</option>
                          </select>
                        ) : getNumericConfig(checkName) ? (
                          <div className={styles.numericControl}>
                            <label className={styles.toggleWrapper}>
                              <input
                                type="checkbox"
                                checked={getCheckValue(checkName) !== false}
                                onChange={() => handleNumericToggle(checkName)}
                                className={styles.toggleInput}
                              />
                              <span className={styles.toggleSlider} />
                            </label>
                            {getCheckValue(checkName) !== false && (
                              <div className={styles.numericSliderRow}>
                                <input
                                  type="range"
                                  className={styles.numericSlider}
                                  min={getNumericConfig(checkName)!.min}
                                  max={getNumericConfig(checkName)!.max}
                                  step={getNumericConfig(checkName)!.step}
                                  value={typeof getCheckValue(checkName) === 'number' ? getCheckValue(checkName) as number : getNumericConfig(checkName)!.default}
                                  onChange={(e) => handleNumericChange(checkName, parseFloat(e.target.value))}
                                />
                                <span className={styles.numericValue}>
                                  {typeof getCheckValue(checkName) === 'number' ? getCheckValue(checkName) : getNumericConfig(checkName)!.default}
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <label className={styles.toggleWrapper}>
                            <input
                              type="checkbox"
                              checked={getCheckValue(checkName) !== false}
                              onChange={() => handleToggle(checkName)}
                              className={styles.toggleInput}
                            />
                            <span className={styles.toggleSlider} />
                          </label>
                        )}
                      </TableCell>
                      <TableCell>
                        <code className={styles.checkName}>{checkName}</code>
                        {isCheckModified(checkName) && (
                          <span className={styles.modifiedBadge}>modified</span>
                        )}
                      </TableCell>
                      <TableCell className={styles.descriptionCell}>
                        {descriptions[checkName]}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          <div className={styles.actions}>
            <Button
              onClick={handleReset}
              disabled={resetMutation.isPending}
            >
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasPendingChanges || updateMutation.isPending}
              loading={updateMutation.isPending}
            >
              Save Changes
              {hasPendingChanges && ` (${Object.keys(pendingChanges).length})`}
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.empty}>Unable to load settings</div>
      )}

      {showResetConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowResetConfirm(false)}>
          <Window title="Reset to Defaults" maxWidth={400} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div className={styles.confirmContent}>
              <p>Are you sure you want to reset all fingerprint checks to their default settings?</p>
              <p className={styles.confirmNote}>This will enable all security checks.</p>
              <div className={styles.confirmActions}>
                <Button type="button" onClick={() => setShowResetConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={confirmReset}
                  loading={resetMutation.isPending}
                >
                  Reset to Defaults
                </Button>
              </div>
            </div>
          </Window>
        </div>
      )}
    </div>
  );
}
